import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTranslation, parseApiError, useSettings } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';

const LoginPage = ({ isRegister = false }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const t = useTranslation();
  const { settings } = useSettings();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created successfully!');
      } else {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(parseApiError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fadeIn">
          <div className="mb-10">
            <h1 className="text-4xl font-bold font-['Outfit'] tracking-tight text-primary">DDS</h1>
            <p className="text-gray-500 mt-2">Dynamic Document System</p>
          </div>

          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-semibold font-['Outfit'] tracking-tight">
                {isRegister ? t('createAccount') : t('welcomeBack')}
              </CardTitle>
              <CardDescription>
                {isRegister 
                  ? 'Enter your details to create your account'
                  : t('signInToContinue')
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('fullName')}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10 h-11"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-11"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t('password')}</Label>
                    {!isRegister && (
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-primary transition-colors hover:opacity-80"
                        data-testid="forgot-password-link"
                      >
                        {t('forgotPassword')}
                      </Link>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 h-11"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 transition-all active:scale-[0.98]"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      {isRegister ? t('register') : t('login')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-gray-600">
                {isRegister ? (
                  <>
                    {t('alreadyHaveAccount')}{' '}
                    <Link to="/login" className="text-primary font-medium hover:opacity-80" data-testid="switch-to-login">
                      {t('login')}
                    </Link>
                  </>
                ) : (
                  <>
                    {t('dontHaveAccount')}{' '}
                    <Link to="/register" className="text-primary font-medium hover:opacity-80" data-testid="switch-to-register">
                      {t('register')}
                    </Link>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Panel - Image/Brand */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12"
        style={{ background: `linear-gradient(to bottom right, var(--color-primary), var(--color-primary-dark))` }}
      >
        <div className="text-white max-w-md animate-fadeIn">
          <h2 className="text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
            Transform your documents into digital assets
          </h2>
          <p className="text-white/80 text-lg leading-relaxed">
            Dynamic Document System helps you manage, track, and approve documents with ease. 
            Built for modern enterprises.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold font-['Outfit']">100%</div>
              <div className="text-white/70 text-sm">Digital Workflow</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold font-['Outfit']">24/7</div>
              <div className="text-white/70 text-sm">Document Access</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
